import React, { useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import {
	Card,
	CardBody,
	CardFooter,
	Form,
	InputGroupAddon,
	InputGroupText,
	InputGroup,
} from "reactstrap";
import Logo from "assets/img/logo-dark.svg";
import crypto from "utils/crypto";
import { getUserDetails, sendMFA, verifyMFA } from "api/LoginAPI";
import { login } from "utils/authUtils";
import { loginFailAlert, failAlert } from "utils/alertUtils";
import { PATHS, bhphPrivileges } from "constants/Constants";
import { DC_Login_Logo, DC_CompanyName, BrandCode } from "../../../config";

const LoginForm = () => {
	const [disabled, setDisabled] = useState(false);
	const [username, setUsername] = useState("");
	const [password, setPassword] = useState("");
	const [mfaCode, setMfaCode] = useState("");
	const [showMfa, setShowMfa] = useState(false);
	const [showMfaOptions, setShowMfaOptions] = useState(false);
	const [showLogin, setShowLogin] = useState(true);
	const history = useHistory();

	const isMountedRef = useRef(null);

	useEffect(() => {
		isMountedRef.current = true;
		return () => {
			isMountedRef.current = false;
		};
	}, []);

	const finalizeLogin = (res) => {
		let resp = res.data.content;

		const cfState = encodeURIComponent(
			crypto.randomBytes(16).toString("base64")
		);

		const privilegesStr = resp.dcLogin.userPrivileges;
		const privileges = JSON.parse(privilegesStr);
		// if they have no bhph privileges then add them with all privileges
		if (!privileges.bhphPrivileges) {
			let allBhphPriv = { ...bhphPrivileges };
			Object.keys(allBhphPriv).forEach((key) => {
				allBhphPriv[key] = true;
			});
			privileges.bhphPrivileges = allBhphPriv;
		}
		resp.dcLogin.userPrivileges = JSON.stringify(privileges);

		const data = {
			...resp.dcLogin,
			cfState,
			locations: resp.locations,
			dealers: resp.dealers,
			jws: resp.jws,
			profileData: resp.profileData,
			messagingNumber: resp.phoneNumber,
		};

		login(JSON.stringify(data));

		if (resp.dcLogin.lastLoginDate === null) {
			history.push(PATHS.UPDATE_PASSWORD);
		} else {
			history.push(PATHS.DASHBOARD);
		}
	};

	const handleSubmit = (e) => {
		e.preventDefault();
		setDisabled(true);
		getUserDetails({ username, password }).then(
			(res) => {
				console.log(res);
				if (res.data.content.locations.salesRepNumber != BrandCode) {
					failAlert("Invalid Brand Code");
					return;
				}
				if (!isMountedRef.current) return;
				if (res.status === 200) {
					if (res.data.content === null) {
						setShowLogin(false);
						setShowMfaOptions(true);
					} else {
						finalizeLogin(res);
					}
				}
				setDisabled(false);
			},
			(error) => {
				if (!isMountedRef.current) return;
				if (error?.response?.data?.message) {
					failAlert(error.response.data.message);
				} else {
					loginFailAlert();
				}
				setDisabled(false);
			}
		);
	};

	const handleMfaOptionSubmit = (isText) => {
		setDisabled(true);
		setShowMfaOptions(false);
		sendMFA({ username, password, isText })
			.then(
				(res) => {
					if (!isMountedRef.current) return;
					if (res.status === 200) {
						setShowMfa(true);
					}
					setDisabled(false);
				},
				(error) => {
					if (!isMountedRef.current) return;
					if (error?.response?.data?.message) {
						failAlert(error.response.data.message);
					} else {
						loginFailAlert();
					}
					setDisabled(false);
					setShowMfaOptions(true);
				}
			)
			.catch((error) => {
				if (!isMountedRef.current) return;
				failAlert("Unknown Error occurred");
				setDisabled(false);
			});
	};

	const handleMfaSubmit = (e) => {
		e.preventDefault();
		setDisabled(true);
		verifyMFA({ username, mfaCode })
			.then(
				(res) => {
					if (!isMountedRef.current) return;
					if (res.status === 200) {
						finalizeLogin(res);
					}
					setDisabled(false);
				},
				(error) => {
					if (!isMountedRef.current) return;
					if (error?.response?.data?.message) {
						failAlert(error.response.data.message);
					} else {
						loginFailAlert();
					}
					setDisabled(false);
				}
			)
			.catch((error) => {
				if (!isMountedRef.current) return;
				failAlert("Unknown Error occurred");
				setDisabled(false);
			});
	};

	return (
		<div className="login-page">
			<Form
				className="form login-form"
				onSubmit={showMfa ? handleMfaSubmit : handleSubmit}
			>
				<Card className="card-login">
					<img
						src={DC_Login_Logo}
						alt={DC_CompanyName + " Logo"}
						className="logo mt-3"
					/>
					<CardBody className="px-4">
						{showMfaOptions && (
							<>
								<p className="text-center">
									Send text or send email for Two-factor authentication?
								</p>
								<div className="text-center mt-1">
									<button
										type="button"
										className="btn btn-primary mx-2 px-4"
										disabled={disabled}
										onClick={() => {
											setShowMfa(true);
											handleMfaOptionSubmit(true);
										}}
									>
										Text
									</button>

									<button
										type="button"
										className="btn btn-primary mx-2 px-4"
										disabled={disabled}
										onClick={() => {
											setShowMfa(true);
											handleMfaOptionSubmit(false);
										}}
									>
										Email
									</button>
								</div>
							</>
						)}
						{showMfa && (
							<>
								<br></br>
								<p className="text-center">Enter code</p>
								<InputGroup className="mt-2">
									<InputGroupAddon addonType="prepend">
										<InputGroupText>
											<i className="nc-icon nc-email-85 text-primary" />
										</InputGroupText>
									</InputGroupAddon>
									<input
										type="text"
										className="form-control"
										id="mfaCode"
										placeholder="Enter OTP code"
										value={mfaCode}
										onChange={(e) => setMfaCode(e.target.value)}
									/>
								</InputGroup>
								<div className="text-center mt-3">
									<button
										type="submit"
										className="btn btn-primary mx-2"
										disabled={disabled}
									>
										Verify Code
									</button>
								</div>
								<div className="text-center mt-1">
									<button
										type="button"
										style={{
											fontSize: "0.8rem",
											textDecoration: "underline",
											color: "#007bff",
											cursor: "pointer",
										}}
										className="btn underline btn-link text-center text-sm mx-auto"
										disabled={disabled}
										onClick={() => {
											setMfaCode("");
											setShowMfa(false);
											setShowMfaOptions(false);
											setShowLogin(true);
											setDisabled(false);
										}}
									>
										Start Over
									</button>
								</div>
							</>
						)}
						{showLogin && (
							<>
								<InputGroup>
									<InputGroupAddon addonType="prepend">
										<InputGroupText>
											<i className="nc-icon nc-single-02 text-primary" />
										</InputGroupText>
									</InputGroupAddon>
									<input
										type="text"
										className="form-control"
										id="username"
										placeholder="Enter username"
										value={username}
										onChange={(e) => setUsername(e.target.value)}
									/>
								</InputGroup>
								<InputGroup className="mt-2">
									<InputGroupAddon addonType="prepend">
										<InputGroupText>
											<i className="nc-icon nc-key-25 text-primary" />
										</InputGroupText>
									</InputGroupAddon>
									<input
										type="password"
										className="form-control"
										id="password"
										placeholder="Password"
										value={password}
										onChange={(e) => setPassword(e.target.value)}
									/>
								</InputGroup>
								<div className="text-center mt-3">
									<button
										type="submit"
										className="btn btn-primary mx-auto"
										disabled={disabled}
									>
										Login
									</button>
								</div>
							</>
						)}
					</CardBody>

					{showLogin && (
						<CardFooter className="text-center">
							<div>
								<a
									className="mx-2 text-center"
									href="/"
									onClick={(e) => {
										e.preventDefault();
										history.push(PATHS.FORGOTTEN_PASSWORD);
									}}
								>
									I forgot my password...
								</a>
							</div>
							<hr className="mt-2 pb-2" />
							<a
								href="/#"
								onClick={(e) => {
									e.preventDefault();
									history.push(PATHS.NEW_USER);
								}}
							>
								Click here to activate new account
							</a>
						</CardFooter>
					)}
				</Card>
			</Form>
		</div>
	);
};

export default LoginForm;
